"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ProgressStepper = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _customPropTypes = _interopRequireDefault(require("../../util/custom-prop-types"));
var _parseSteps = require("../../util/parseSteps");
var _ProgressBar = require("../ProgressBar/ProgressBar");
var _ProgressStepperCurrentLabel = require("../ProgressStepperCurrentLabel/ProgressStepperCurrentLabel");
var _ProgressStepperStep = require("../ProgressStepperStep/ProgressStepperStep");
var _ProgressStepper = require("./ProgressStepper.styles");
var _excluded = ["activeStepIndex", "alignLabels", "mapStepsToProps", "labelAppearance", "onStepClick", "orientation", "renderProgressStepperStep", "steps"],
  _excluded2 = ["interactive", "label", "onClick"];
/**
 * @related ['ProgressBar']
 * @subcomponents ['ProgressStepperStep', 'ProgressStepperStepLabel']
 * @since 8.42.0
 */
var ProgressStepper = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var activeStepIndex = _ref.activeStepIndex,
    alignLabels = _ref.alignLabels,
    mapStepsToProps = _ref.mapStepsToProps,
    labelAppearance = _ref.labelAppearance,
    onStepClick = _ref.onStepClick,
    orientation = _ref.orientation,
    renderProgressStepperStep = _ref.renderProgressStepperStep,
    stepsProp = _ref.steps,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var steps = (0, _react.useMemo)(function () {
    return (0, _parseSteps.parseSteps)(stepsProp, mapStepsToProps);
  }, [mapStepsToProps, stepsProp]);
  var currentLabel = '';
  var currentMeta = '';
  if (activeStepIndex >= 0 && activeStepIndex < steps.length) {
    currentLabel = steps[activeStepIndex].label;
    currentMeta = steps[activeStepIndex].meta;
  }
  var onClickCallBack = (0, _react.useCallback)(function (e, index, _ref2) {
    var interactive = _ref2.interactive,
      onClick = _ref2.onClick;
    if (interactive && typeof onStepClick === 'function') {
      onStepClick(e, index);
    }
    if (typeof onClick === 'function') {
      onClick(e);
    }
  }, [onStepClick]);
  var renderStepCallback = (0, _react.useCallback)(function (step, index) {
    var interactive = step.interactive,
      label = step.label,
      onClick = step.onClick,
      stepRest = (0, _objectWithoutProperties2.default)(step, _excluded2);
    return renderProgressStepperStep(Object.assign({
      active: index === activeStepIndex,
      alignLabel: alignLabels,
      interactive: interactive,
      key: index + label,
      label: label,
      onClick: function onClick(e) {
        return onClickCallBack(e, index, step);
      },
      orientation: orientation
    }, stepRest));
  }, [activeStepIndex, alignLabels, onClickCallBack, orientation, renderProgressStepperStep]);
  return /*#__PURE__*/_react.default.createElement(_ProgressStepper.StyledProgressStepper, (0, _extends2.default)({
    activeStepIndex: activeStepIndex,
    alignLabels: alignLabels,
    length: steps.length,
    labelAppearance: labelAppearance,
    orientation: orientation,
    ref: forwardedRef
  }, rest), /*#__PURE__*/_react.default.createElement(_ProgressBar.ProgressBar, {
    "aria-label": "Step " + (Number(activeStepIndex) + 1) + " of " + steps.length,
    "aria-hidden": "true",
    minValue: 0,
    maxValue: steps.length - 1,
    orientation: orientation,
    size: _constants.PROGRESS_METER_SIZES.sm,
    value: activeStepIndex > 0 ? activeStepIndex : 0
  }), steps.map(renderStepCallback), /*#__PURE__*/_react.default.createElement(_ProgressStepperCurrentLabel.ProgressStepperCurrentLabel, {
    label: currentLabel,
    meta: currentMeta
  }));
});
exports.ProgressStepper = ProgressStepper;
ProgressStepper.displayName = 'ProgressStepper';
ProgressStepper.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Zero-based index of the step the user is currently on. This step will
   * display in the "active" state.
   *
   * If you don't want any steps to display as "active", you can use a value either
   * less than `0` or higher than the final step's index. Ex: If the user hasn't started
   * any steps yet, set `activeStepIndex={-1}` and the bar color will indicate no
   * progress. If the user has completed all of five steps, set `activeStepIndex={5}`
   * or higher.
   */
  'activeStepIndex': _propTypes.default.number,
  /**
   * Sets the horizontal alignment for all step labels.
   *
   * By default, a horizontal ProgressStepper center-aligns the labels. However, when
   * using labels that span multiple lines (ex: a label with a status tag underneath),
   * left-aligning the labels may make them more readable.
   *
   * Note that this only affects a ProgressStepper with horizontal orientation. A
   * vertical ProgressStepper will always have left-aligned labels.
   *
   * Supports inline media query objects.
   *
   * @since 8.57.0
   */
  'alignLabels': _customPropTypes.default.inlineMediaQuery(_propTypes.default.oneOf(Object.values(_constants.PROGRESS_STEPPER_LABEL_ALIGNMENTS))),
  /**
   * A unique label that describes this component's purpose.
   * Especially necessary if you have multiple ProgressSteppers on the same page.
   *
   * @since 8.57.0
   */
  'aria-label': _propTypes.default.string,
  /**
   * Option to show all step labels, only the current step label, or hide all step labels
   * on different breakpoints. Values can be one of "all", "current", or "none".
   *
   * Supports inline media query objects.
   */
  'labelAppearance': _customPropTypes.default.inlineMediaQuery(_propTypes.default.oneOf(Object.values(_constants.PROGRESS_STEPPER_LABEL_APPEARANCES))),
  /**
   * A function that maps the values passed to `steps` to an object of props that will be
   * passed to `renderProgressStepperStep`.
   *
   * By default, `mapStepsToProps` will convert strings into an object where the string is
   * a `label`.
   */
  'mapStepsToProps': _propTypes.default.func,
  /**
   * Called when an interactive step is clicked.
   *
   * This handler will emit the click event as the first
   * argument and the step index as the second.
   */
  'onStepClick': _propTypes.default.func,
  /**
   * Displays either the horizontal or vertical version of the ProgressStepper.
   *
   * Supports inline media query objects. Ex: You can display
   * the horizontal version on desktop and tablet and the vertical version on phone.
   *
   * @since 8.57.0
   */
  'orientation': _customPropTypes.default.inlineMediaQuery(_propTypes.default.oneOf(Object.values(_constants.ORIENTATIONS))),
  /**
   * A function that receives `props` as the only argument and returns a step component, by
   * default a `ProgressStepperStep`.
   */
  'renderProgressStepperStep': _propTypes.default.func,
  /**
   * ARIA roles provide semantic meaning to content, allowing screen readers and other tools to
   * present and support interaction with object in a way that is consistent with user
   * expectations of that type of object.
   *
   * In this component we feel that `group` is the correct choice
   *
   * @since 8.79.5
   */
  'role': _propTypes.default.string,
  /**
   * An array of step information.
   *
   * Providing an array of strings will create a simple Progress Stepper with no icons
   * nor interactivity.
   *
   * For a more complex Progress Stepper, you can provide an array of objects that will
   * be passed as props to `ProgressStepperStep`.
   * Objects must contain at least a `label` property (or, if you're using a custom
   * `mapStepsToProps` callback, something equivalent). You can also use the optional `meta`
   * property to show additional content underneath a step label (ex: a `Tag` or `Text`)
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  'steps': _propTypes.default.array.isRequired
} : {};
ProgressStepper.defaultProps = {
  'activeStepIndex': 0,
  'alignLabels': _constants.PROGRESS_STEPPER_LABEL_ALIGNMENTS.CENTER,
  'aria-label': 'progress',
  'labelAppearance': {
    default: _constants.PROGRESS_STEPPER_LABEL_APPEARANCES.ALL,
    sm_lte: _constants.PROGRESS_STEPPER_LABEL_APPEARANCES.CURRENT
  },
  'orientation': _constants.ORIENTATIONS.horizontal,
  'renderProgressStepperStep': function renderProgressStepperStep(props) {
    return /*#__PURE__*/_react.default.createElement(_ProgressStepperStep.ProgressStepperStep, props);
  },
  'role': 'group'
};
ProgressStepper.APPEARANCES = _constants.PROGRESS_STEPPER_APPEARANCES;
ProgressStepper.LABEL_ALIGNMENTS = _constants.PROGRESS_STEPPER_LABEL_ALIGNMENTS;
ProgressStepper.LABEL_APPEARANCES = _constants.PROGRESS_STEPPER_LABEL_APPEARANCES;